/**
 * Anmeldesitzung im Browser – gemeinsam für Web und Mobil: Token-Speicher,
 * Auslieferungspfad und Token-Erneuerung mit Abstimmung der Tabs.
 *
 * Beide Oberflächen unterscheiden sich nur im Schlüsselpräfix des
 * `localStorage` (`ff` / `ffm`), damit sich Web und Mobil unter demselben
 * Ursprung nicht gegenseitig abmelden.
 */

export function createSession(prefix: string) {
  const ACCESS_KEY = `${prefix}.accessToken`;
  const REFRESH_KEY = `${prefix}.refreshToken`;

  const tokenStore = {
    get access() {
      return localStorage.getItem(ACCESS_KEY);
    },
    get refresh() {
      return localStorage.getItem(REFRESH_KEY);
    },
    set(access: string, refresh?: string) {
      localStorage.setItem(ACCESS_KEY, access);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    },
    clear() {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
    },
  };

  /**
   * Setzt einen API-Pfad auf den Auslieferungspfad der Anwendung auf.
   *
   * `import.meta.env.BASE_URL` ist im Normalfall „/“ – dann ändert sich nichts.
   * Wird die Anwendung aber unter einem Unterpfad ausgeliefert, etwa hinter
   * einem Reverse Proxy unter `/facilityflowmanager/`, dann zeigte ein fest
   * ab „/“ gebauter Pfad an der Anwendung vorbei auf die Domainwurzel.
   *
   * Vite garantiert, dass `BASE_URL` auf „/“ endet; der Pfad wird deshalb ohne
   * führenden Schrägstrich angehängt, damit keine doppelten entstehen.
   *
   * Gebaut wird ein solcher Stand mit `vite build --base=/irgendein-pfad/`.
   */
  function apiUrl(path: string): URL {
    const base = import.meta.env.BASE_URL || '/';
    return new URL(`${base}${path.replace(/^\/+/, '')}`, window.location.origin);
  }

  /*
   * Abstimmung der Tabs bei der Token-Erneuerung (Review M3).
   *
   * Seit Review M3 beendet ein zweites Vorlegen desselben Refresh-Tokens
   * **alle** Sitzungen – das ist das einzige verlässliche Zeichen für einen
   * kopierten Token. Ohne Abstimmung wäre genau das der Alltag: Jeder Tab hat
   * seinen eigenen `refreshPromise`, liest denselben Token aus dem
   * `localStorage` und legt ihn gleichzeitig vor. Der Zweite löste die
   * Abmeldung aus.
   *
   * Deshalb erneuert immer nur *ein* Tab, und die anderen übernehmen sein
   * Ergebnis. Ein Zeitfenster („Wiederverwendung innerhalb von 10 Sekunden ist
   * ein Rennen, kein Diebstahl“) wäre die Notlösung gewesen – sie hätte den
   * Befund halb offen gelassen.
   */
  const ANSPRUCH_KEY = `${prefix}.refreshClaim`;
  const ANSPRUCH_GILT_MS = 10_000;
  const EIGENE_KENNUNG = Math.random().toString(36).slice(2);

  /**
   * Versucht, die Erneuerung für diesen Tab zu beanspruchen.
   *
   * Schreiben und Nachlesen: Schreiben mehrere Tabs gleichzeitig, gewinnt der
   * letzte, und die übrigen sehen beim Nachlesen eine fremde Kennung. Das ist
   * kein echter Mutex, aber `localStorage`-Schreibvorgänge sind je Ursprung
   * serialisiert – für zwei bis fünf offene Fenster reicht es, und mehr sind
   * hier der Fall.
   *
   * Ein Anspruch verfällt nach zehn Sekunden. Ohne diesen Verfall blockierte
   * ein Tab, der mitten im Vorgang geschlossen wird, alle anderen dauerhaft.
   */
  function beansprucheErneuerung(): boolean {
    const jetzt = Date.now();
    try {
      const roh = localStorage.getItem(ANSPRUCH_KEY);
      const stand = roh ? (JSON.parse(roh) as { von: string; zeit: number }) : null;
      if (stand && jetzt - stand.zeit < ANSPRUCH_GILT_MS) return false;
      localStorage.setItem(ANSPRUCH_KEY, JSON.stringify({ von: EIGENE_KENNUNG, zeit: jetzt }));
      const nachher = JSON.parse(localStorage.getItem(ANSPRUCH_KEY) ?? '{}') as { von?: string };
      return nachher.von === EIGENE_KENNUNG;
    } catch {
      // Kein oder gesperrter localStorage (privates Fenster, blockierte
      // Site-Daten): Dann lieber erneuern als gar nichts tun.
      return true;
    }
  }

  function gibAnspruchFrei(): void {
    try {
      const stand = JSON.parse(localStorage.getItem(ANSPRUCH_KEY) ?? '{}') as { von?: string };
      if (stand.von === EIGENE_KENNUNG) localStorage.removeItem(ANSPRUCH_KEY);
    } catch {
      /* nichts zu tun */
    }
  }

  /**
   * Wartet darauf, dass ein anderer Tab den Token erneuert hat.
   *
   * Erkennbar daran, dass im `localStorage` ein anderer Refresh-Token steht als
   * der, mit dem dieser Tab gestartet ist. Nach fünf Sekunden wird aufgegeben –
   * dann ist der andere Tab vermutlich weg, und der nächste 401 versucht es neu.
   */
  async function warteAufErneuerung(vorher: string): Promise<boolean> {
    for (let versuch = 0; versuch < 50; versuch += 1) {
      await new Promise((fertig) => setTimeout(fertig, 100));
      const jetzt = tokenStore.refresh;
      if (jetzt && jetzt !== vorher) return true;
      if (!jetzt) return false; // abgemeldet
    }
    return false;
  }

  let refreshPromise: Promise<boolean> | null = null;

  async function tryRefresh(): Promise<boolean> {
    const refreshToken = tokenStore.refresh;
    if (!refreshToken) return false;

    refreshPromise ??= (async () => {
      // Nur ein Tab erneuert; die übrigen übernehmen sein Ergebnis (Review M3).
      if (!beansprucheErneuerung()) return await warteAufErneuerung(refreshToken);

      try {
        const response = await fetch(apiUrl('/api/auth/refresh').toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) return false;
        const data = await response.json();
        tokenStore.set(data.accessToken, data.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        gibAnspruchFrei();
        setTimeout(() => {
          refreshPromise = null;
        }, 0);
      }
    })();

    return refreshPromise;
  }

  return { tokenStore, apiUrl, tryRefresh };
}
